"use client";

import { useEffect, useRef, useState } from "react";

interface Testimonial {
  nameEn: string;
  nameZh: string;
  locationEn: string;
  locationZh: string;
  textEn: string;
  textZh: string;
  rating: number;
  projectType: string;
  area: string;
}

const TESTIMONIALS: Testimonial[] = [
  {
    nameEn: "Mrs. Chan Wai Ling",
    nameZh: "陳惠玲女士",
    locationEn: "Mong Kok, Kowloon",
    locationZh: "九龍旺角",
    textEn: "APEX transformed our 40-year-old flat into a stunning modern home. The team was professional, on-time, and the quality exceeded our expectations. Highly recommend!",
    textZh: "高峯將我們40年樓齡的舊居翻新成現代化美宅。團隊專業、準時，工程質量超出預期，強力推薦！",
    rating: 5,
    projectType: "Full Home Renovation",
    area: "850 sq ft",
  },
  {
    nameEn: "Mr. Lee Ka Fai",
    nameZh: "李家輝先生",
    locationEn: "Sham Shui Po",
    locationZh: "深水埗",
    textEn: "Transparent pricing and no surprises. They delivered exactly what was quoted, on budget and two weeks early. The custom wardrobes are absolutely beautiful.",
    textZh: "報價透明，無意外收費。完全按報價施工，在預算內，還提早兩星期完工。訂造衣櫃非常漂亮。",
    rating: 5,
    projectType: "Bedroom & Custom Furniture",
    area: "420 sq ft",
  },
  {
    nameEn: "Ms. Ng Siu Man",
    nameZh: "吳小文小姐",
    locationEn: "Yau Ma Tei",
    locationZh: "油麻地",
    textEn: "The kitchen renovation completely changed how we cook and entertain. APEX's attention to detail and use of premium materials made all the difference.",
    textZh: "廚房翻新完全改變了我們烹飪和招待朋友的方式。高峯對細節的重視和優質材料的運用，令人印象深刻。",
    rating: 5,
    projectType: "Kitchen Renovation",
    area: "200 sq ft",
  },
  {
    nameEn: "Mr. Wong Tsz Ho",
    nameZh: "黃子浩先生",
    locationEn: "Tsim Sha Tsui",
    locationZh: "尖沙咀",
    textEn: "We hired APEX for our office renovation in TST. The project manager was excellent — always responsive and kept everything on track. Our staff love the new workspace.",
    textZh: "我們委託高峯翻新尖沙咀辦公室。項目經理出色，隨時回應，確保工程順利進行。員工非常喜歡新工作環境。",
    rating: 5,
    projectType: "Commercial Office",
    area: "3,200 sq ft",
  },
  {
    nameEn: "Mrs. Lam Pui Yee",
    nameZh: "林佩儀女士",
    locationEn: "Jordan, Kowloon",
    locationZh: "九龍佐敦",
    textEn: "The bathroom renovation turned our tiny HK bathroom into a spa-like retreat. The waterproofing is perfect — no leaks at all. Worth every penny.",
    textZh: "浴室翻新將我們細小的香港浴室變成水療式休閒空間。防水工程完美，完全無滲漏，物超所值。",
    rating: 5,
    projectType: "Bathroom Renovation",
    area: "65 sq ft",
  },
  {
    nameEn: "Mr. Cheung Chun Wai",
    nameZh: "張俊威先生",
    locationEn: "Mong Kok",
    locationZh: "旺角",
    textEn: "Second time using APEX — first for our home, now for our shop in Mong Kok. Consistent quality, reliable team, and their bilingual communication made everything smooth.",
    textZh: "第二次使用高峯服務——第一次裝修住宅，今次裝修旺角店鋪。質量一貫，團隊可靠，中英雙語溝通順暢。",
    rating: 5,
    projectType: "Residential + Commercial",
    area: "700 sq ft",
  },
];

export default function TestimonialsSection() {
  const [activeIndex, setActiveIndex] = useState(0);
  const sectionRef = useRef<HTMLElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const elements = sectionRef.current?.querySelectorAll(".test-reveal");
    if (!elements) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.remove("hidden-reveal");
        });
      },
      { threshold: 0.1 }
    );
    elements.forEach((el) => {
      el.classList.add("hidden-reveal");
      observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const scrollToCard = (index: number) => {
    setActiveIndex(index);
    const card = scrollRef.current?.children[index] as HTMLElement;
    card?.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
  };

  return (
    <section
      id="testimonials"
      ref={sectionRef}
      className="py-24 md:py-32 relative overflow-hidden"
      style={{ background: "linear-gradient(180deg, #0D1E3A 0%, #0A1628 100%)" }}
    >
      {/* Background decoration */}
      <div
        className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(212,175,55,0.05) 0%, transparent 70%)" }}
      />

      <div className="px-6 md:px-12 max-w-screen-xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16 test-reveal reveal">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="w-8 h-[1px]" style={{ background: "#D4AF37" }} />
            <span className="text-xs font-bold uppercase tracking-[0.2em]" style={{ color: "#D4AF37" }}>
              Testimonials · 客戶評價
            </span>
            <div className="w-8 h-[1px]" style={{ background: "#D4AF37" }} />
          </div>
          <h2 className="font-display section-title text-white">
            客戶的信賴<br />
            <span className="italic" style={{ color: "#D4AF37" }}>Real Stories</span>, Real Results
          </h2>
        </div>
      </div>

      {/* Testimonials Horizontal Scroll */}
      <div
        ref={scrollRef}
        className="flex gap-6 overflow-x-auto no-scrollbar px-6 md:px-12 pb-6 snap-x snap-mandatory"
      >
        {TESTIMONIALS.map((t, index) => (
          <div
            key={`${t.nameEn}-${index}`}
            className="testimonial-card min-w-[300px] md:min-w-[420px] snap-start rounded-2xl p-8 flex-shrink-0 flex flex-col gap-4 cursor-pointer"
            onClick={() => scrollToCard(index)}
          >
            {/* Stars */}
            <div className="flex gap-1">
              {Array.from({ length: t.rating }).map((_, i) => (
                <svg key={i} width="16" height="16" viewBox="0 0 24 24" fill="#D4AF37">
                  <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                </svg>
              ))}
            </div>

            {/* Quote */}
            <div className="flex-1">
              <p className="text-base leading-relaxed text-white font-medium mb-3">"{t.textEn}"</p>
              <p className="text-xs leading-relaxed" style={{ color: "rgba(245,240,232,0.5)" }}>「{t.textZh}」</p>
            </div>

            {/* Divider */}
            <div className="gold-line opacity-20" />

            {/* Author */}
            <div className="flex items-center justify-between">
              <div>
                <div className="font-bold text-white text-sm">{t.nameZh}</div>
                <div className="text-xs" style={{ color: "rgba(245,240,232,0.5)" }}>{t.nameEn}</div>
                <div className="text-xs mt-0.5" style={{ color: "#D4AF37" }}>{t.locationZh}</div>
              </div>
              <div className="text-right">
                <div
                  className="text-xs font-bold px-3 py-1 rounded-full"
                  style={{ background: "rgba(212,175,55,0.1)", color: "#D4AF37", border: "1px solid rgba(212,175,55,0.2)" }}
                >
                  {t.projectType}
                </div>
                <div className="text-xs mt-1" style={{ color: "rgba(245,240,232,0.4)" }}>{t.area}</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Dot Indicators */}
      <div className="flex justify-center gap-2 mt-8 px-6">
        {TESTIMONIALS.map((_, index) => (
          <button
            key={index}
            onClick={() => scrollToCard(index)}
            className="transition-all duration-300 rounded-full"
            style={{
              width: activeIndex === index ? "24px" : "8px",
              height: "8px",
              background: activeIndex === index ? "#D4AF37" : "rgba(212,175,55,0.3)",
            }}
            aria-label={`Go to testimonial ${index + 1}`}
          />
        ))}
      </div>
    </section>
  );
}